import React, { useEffect, useRef, useState } from 'react'
import Counter from '../../baseComponents/gui/counter/Counter'
import useStateReducer from '../../../utils/scene/react/hooks/useStateReducer'
import { getNextState } from '../../../utils/scene/utils/state/state'
import { useGamesModal } from '../../../hooks/useGamesModal'
import CustomButton from '../../features/customButton/ui/main/CustomButton'
import { image } from '../../../utils/data/baseUrl'
import { isJumperForMeAssets } from '../../../utils/game/isJumperForMeAssets'
import { isJumperAdidasAssets } from '../../../utils/game/isJumperAdidasAssets'
import { isJumperRimmelAssets } from '../../../utils/game/isJumperRimmelAssets'
import AppLoader from '../../features/appLoader/AppLoader'
import classNames from 'classnames'
import { ignoreNextStates, states } from '../../../constants/game-doodle-settings'
import { infinityLevel, settings } from '../../../constants/doodle'
import { Stats } from '../../features/stats'
import { useRequests } from '../../../redux/reducer/requests'
import useGameLog from '../../../hooks/useGameLog'
import { useGameControls } from '../../../hooks/useGameControls'
import { useContent } from '../../../redux/reducer/content'
import { useBoostersTutorial } from '../../../hooks/useBoostersTutorial'
import { GameWrapper } from '../../features/gameWrapper/GameWrapper'
import { eventSubscription } from '../../../utils/events/eventSubscription'
import { usePreloadedGameWrapper } from '../../../hooks/usePreloadedGameWrapper'

const currentLevel = infinityLevel

const defaultGameData = {
  score: 0,
  lifes: 1,
  maxLifes: 1,
  coins: 0,
  targetCoins: 50,
  maxScore: currentLevel?.maxScore,
}

const Doodle = React.forwardRef(function Doodle({ callbacks }, ref) {
  const {
    authData: { isWeb },
  } = useRequests()
  const { gameDifficulty } = useContent()
  const preloadGameWrapper = usePreloadedGameWrapper('jumper') // предзагруженный wrapper

  // Контейнер куда будет монтироваться сanvas
  const containerRef = useRef()
  const [state, setState] = useState('loadManifest')
  const [wrapper, setWrapper] = useState(null)
  const [gameData, setGameData] = useState(defaultGameData)
  // Таймер обратного отсчёта перед началом уровня
  const [gameTimer, setGameTimer] = useState(3)

  // Логирование событий игры
  const logs = useGameLog(wrapper?.eventBus)

  // Сбрасывает игру до начального состояния (используется при проигрыше или ручном сбросе)
  const resetCallback = () => {
    wrapper.setDefault()
    setState('initLevel')
    setGameData(defaultGameData)
  }

  // Основная логика управления игрой (обработка победы, поражения, подсчёт очков)
  const { win } = useGameControls({
    wrapper,
    isInfinity: true,
    gameType: 'jumper',
    logs,
    gameData,
    callbacks,
  })

  // Управление модальными окнами (пауза, ожидание загрузки, обработка проигрыша)
  const { openPauseModal, isPending } = useGamesModal({
    game: 'jumper',
    callbacks,
    gameState: state,
    setGameState: setState,
    loseCallback: win,
    resetCallback,
  })

  // Указываем что делаем при переходе в состояние игры (при паузе показываем модальное окно и тп)
  useStateReducer(
    {
      loadManifest: () =>
        (wrapper.settings = { ...settings, probabilityDistributions: currentLevel.modules }),
      loadComplete: () => wrapper.controller.appendContainer(containerRef.current),
      initLevel: () => setGameTimer(3),
      showing: () => setState(getNextState(states, state)),
      paused: () => openPauseModal(),
      lose: win,
      win: win,
    },
    ignoreNextStates,
    (state) => setState(getNextState(states, state)),
    state,
    wrapper,
  )

  // Хук для отображения модального окна при захвате бустера с пояснением его свойств
  useBoostersTutorial({
    wrapper,
    toTutorial: () => {
      setState('boosterTutorial')
    },
    toPlaying: () => {
      setGameTimer(3)
      setState('countdown')
    },
    type: 'jumper',
  })

  // Подставляем предзагруженный wrapper из стора в локальный компонент
  useEffect(() => {
    if (preloadGameWrapper && !wrapper) {
      setWrapper(preloadGameWrapper)
    }
  }, [preloadGameWrapper]) // Срабатывает, когда предзагруженный wrapper становится доступен

  // Инициализация wrapper после его загрузки (настройка сложности и цели монет)
  useEffect(() => {
    if (!wrapper || !containerRef.current) {
      return
    }

    wrapper.hidden = false
    wrapper.setDifficulty(gameDifficulty)
    wrapper.setTargetCoins(gameData.targetCoins)
    wrapper.controller.appendContainer(containerRef.current)
  }, [wrapper, gameDifficulty])

  // Подписка на события игрового движка (потеря жизни, изменение счёта, монет и тд)
  useEffect(() => {
    if (!wrapper) {
      return
    }

    const { eventBus } = wrapper

    return eventSubscription({
      target: eventBus,
      callbacksBus: [
        {
          event: 'lose',
          callback() {
            setState('lose')
          },
        },
        {
          event: 'change-score',
          callback({ data: { score } }) {
            setGameData((prev) => ({ ...prev, score }))
          },
        },
        {
          event: 'change-coins',
          callback({ data: { coins } }) {
            setGameData((prev) => ({ ...prev, coins }))
          },
        },
        {
          event: 'change-lifes',
          callback({ data: { lifes } }) {
            setGameData((prev) => {
              const isSubtract = lifes < prev.lifes

              isSubtract && lifes && setState('initLevel')

              return { ...prev, lifes }
            })
          },
        },
      ],
    })
  }, [wrapper]) // Подписываемся только когда предзагруженный wrapper становится доступен

  // Очистка при размонтировании (сбрасываем и скрываем игру)
  useEffect(
    () => () => {
      if (!wrapper) {
        return
      }

      wrapper.setDefault()
      wrapper.hidden = true
    },
    [wrapper],
  )

  const getLogoImage = () => {
    if (isJumperForMeAssets()) {
      return 'games/doodle-logo.png'
    }

    if (isJumperAdidasAssets()) {
      return 'games/doodle-adidas-logo.png'
    }

    if (isJumperRimmelAssets()) {
      return 'games/doodle-rimmel-logo.png'
    }

    return 'games/old/doodle-logo.png'
  }

  const logoImage = getLogoImage()

  return (
    <GameWrapper>
      <div
        className={classNames('doodle-wrapper', {
          'for-me': isJumperForMeAssets(),
          adidas: isJumperAdidasAssets(),
          rimmel: isJumperRimmelAssets(),
        })}
      >
        <div className={`doodle__header ${isWeb ? '' : 'app_margin'}`}>
          <CustomButton
            className={'doodle-wrapper__button button_pause button_pause-pink'}
            onClick={() => setState('paused')}
            icon={'buttons/pause'}
          />

          <Stats
            eventBus={wrapper?.eventBus}
            score={gameData.score}
            targetCoins={gameData.targetCoins}
            count={gameData.coins}
            timer={{ count: 30, img: image('games/time/5.png') }}
            type={'doodle'}
          />
        </div>

        <div className={'doodle__logo'}>
          <img
            src={image(logoImage)}
            alt={'doodle-logo'}
          />
        </div>
        <div
          ref={containerRef}
          className={classNames('doodle-view', { 'doodle-view_visible': state === 'playing' })}
        />
        <Counter
          setGameTimer={setGameTimer}
          gameTimer={gameTimer}
          state={state}
          setState={setState}
          mod={'doodle'}
        />
      </div>

      {isPending && <AppLoader />}
    </GameWrapper>
  )
})

export default Doodle
Doodle.propTypes = {}
